import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import api from '../api/axios'
import toast from 'react-hot-toast'
import { Plus, X, Copy, Check, ExternalLink, Trash2 } from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-blue-50 text-blue-600',
  expired: 'bg-gray-100 text-gray-400',
  completed: 'bg-green-50 text-green-600',
  pending: 'bg-yellow-50 text-yellow-600',
  approved: 'bg-green-50 text-green-600',
  returned: 'bg-orange-50 text-orange-600',
}

export default function OnboardingPage() {
  const [links, setLinks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ employeeName: '', employeeEmail: '' })
  const [saving, setSaving] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selected, setSelected] = useState<any>(null)
  const [submission, setSubmission] = useState<any>(null)
  const [loadingSub, setLoadingSub] = useState(false)
  const [reviewNote, setReviewNote] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const [showCreateStaff, setShowCreateStaff] = useState(false)
  const [staffForm, setStaffForm] = useState({ name: '', email: '', username: '', roleName: 'artist', password: '' })
  const [roles, setRoles] = useState<any[]>([])
  const [creatingStaff, setCreatingStaff] = useState(false)

  const fetchLinks = () => {
    setLoading(true)
    api.get('/onboarding/links').then(res => { setLinks(res.data); setLoading(false) }).catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchLinks()
    api.get('/staff/roles').then(res => setRoles(res.data)).catch(() => {})
  }, [])

  const handleCreate = async () => {
    setSaving(true)
    try {
      await api.post('/onboarding/links', form)
      toast.success('Onboarding link created' + (form.employeeEmail ? ' and email sent' : ''))
      setShowForm(false)
      setForm({ employeeName: '', employeeEmail: '' })
      fetchLinks()
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to create link')
    }
    setSaving(false)
  }

  const copyLink = (token: string, id: string) => {
    const url = `${window.location.origin}/onboarding/${token}`
    navigator.clipboard.writeText(url)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const openSubmission = async (submissionId: string) => {
    setLoadingSub(true)
    try {
      const res = await api.get(`/onboarding/submissions/${submissionId}`)
      setSubmission(res.data)
      setReviewNote('')
      setShowCreateStaff(false)
      setStaffForm({
        name: res.data.fullName || '',
        email: res.data.personalEmail || '',
        username: '',
        roleName: 'artist',
        password: ''
      })
    } catch { toast.error('Failed to load submission') }
    setLoadingSub(false)
  }

  const handleReview = async (action: 'approve' | 'return') => {
    if (!submission) return
    setReviewing(true)
    try {
      await api.patch(`/onboarding/submissions/${submission.id}/review`, { action, hrNote: reviewNote })
      toast.success(action === 'approve' ? 'Submission approved!' : 'Sent back for correction')
      setSubmission(null)
      setSelected(null)
      fetchLinks()
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed')
    }
    setReviewing(false)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this onboarding link?')) return
    try {
      await api.delete(`/onboarding/links/${id}`)
      toast.success('Link deleted')
      fetchLinks()
    } catch { toast.error('Failed to delete') }
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Onboarding</h1>
          <p className="text-gray-400 mt-1 text-sm">Manage employee onboarding forms</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2.5 rounded-xl font-medium transition-colors">
          <Plus size={15} /> New Link
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card p-6 space-y-4 border-2 border-orange-200 dark:border-orange-500/20">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">New Onboarding Link</h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Employee Name</label>
              <input className="input" placeholder="John Doe" value={form.employeeName} onChange={e => setForm(f => ({ ...f, employeeName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Employee Email <span className="text-gray-400 font-normal">(to send link)</span></label>
              <input className="input" type="email" placeholder="john@email.com" value={form.employeeEmail} onChange={e => setForm(f => ({ ...f, employeeEmail: e.target.value }))} />
            </div>
          </div>
          <p className="text-xs text-gray-400">Link expires in 7 days. If email is provided, the link will be sent automatically.</p>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="btn-ghost flex-1">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="btn-primary flex-1 disabled:opacity-50">
              {saving ? 'Creating...' : 'Create & Send Link'}
            </button>
          </div>
        </div>
      )}

      {/* Links list */}
      {loading ? (
        <div className="card p-20 text-center text-gray-400">Loading...</div>
      ) : links.length === 0 ? (
        <div className="card p-20 text-center text-gray-400">No onboarding links yet</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 dark:bg-[#1a3050]">
              <tr>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Employee</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Created By</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Expires</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
              {links.map(link => (
                <tr key={link.id} onClick={() => { if (link.submission) { setSelected(link); openSubmission(link.submission.id) } }}
                  className={`transition-colors ${link.submission ? 'cursor-pointer hover:bg-orange-50/30 dark:hover:bg-orange-900/10' : 'hover:bg-gray-50/50'}`}>
                  <td className="px-6 py-4">
                    <p className="font-medium text-navy-800 dark:text-white">{link.employeeName || '—'}</p>
                    <p className="text-xs text-gray-400">{link.employeeEmail || 'No email'}</p>
                    {link.submission && (
                      <span className="text-xs text-orange-400 mt-1 block">Click row to view →</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-500 hidden sm:table-cell">{link.createdBy}</td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[link.status] || 'bg-gray-100 text-gray-500'}`}>
                        {link.status}
                      </span>
                      {link.submission && (
                        <div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[link.submission.status] || 'bg-gray-100 text-gray-500'}`}>
                            {link.submission.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-400 hidden sm:table-cell">
                    {new Date(link.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {link.status === 'active' && (
                        <>
                          <button onClick={() => copyLink(link.token, link.id)}
                            className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Copy link">
                            {copiedId === link.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                          <a href={`/onboarding/${link.token}`} target="_blank" rel="noreferrer"
                            className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Open form">
                            <ExternalLink size={14} />
                          </a>
                        </>
                      )}
                      <button onClick={() => handleDelete(link.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission detail modal */}
      {selected && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setSelected(null); setSubmission(null) }}>
          <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
              <h2 className="font-display font-bold text-navy-900 dark:text-white">Submission Details</h2>
              <button onClick={() => { setSelected(null); setSubmission(null) }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {loadingSub ? (
                <div className="py-12 text-center text-gray-400">Loading...</div>
              ) : submission ? (
                <div className="space-y-4">
                  {[
                    { label: 'Full Name', value: submission.fullName },
                    { label: 'Date of Birth', value: submission.dateOfBirth ? new Date(submission.dateOfBirth).toLocaleDateString('en-IN') : '—' },
                    { label: 'Gender', value: submission.gender },
                    { label: 'Blood Group', value: submission.bloodGroup || '—' },
                    { label: 'Date of Joining', value: submission.dateOfJoining ? new Date(submission.dateOfJoining).toLocaleDateString('en-IN') : '—' },
                    { label: 'Aadhaar Number', value: submission.aadhaarNumber },
                    { label: 'Phone Number', value: submission.phoneNumber },
                    { label: 'Alt Phone', value: submission.altPhoneNumber || '—' },
                    { label: 'Personal Email', value: submission.personalEmail },
                    { label: 'Emergency Contact', value: `${submission.emergencyContactName} (${submission.emergencyRelationship})` },
                    { label: 'Emergency Phone', value: submission.emergencyContactPhone },
                    { label: 'Permanent Address', value: submission.permanentAddress },
                    { label: 'Contact Address', value: submission.contactAddress || 'Same as permanent' },
                    { label: 'Bank Account', value: submission.bankAccountNumber },
                    { label: 'IFSC Code', value: submission.bankIfscCode },
                    { label: 'Branch Name', value: submission.bankBranchName },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex gap-4 text-sm border-b border-gray-50 dark:border-[#1e3a5f] pb-3">
                      <p className="text-gray-400 w-40 flex-shrink-0">{label}</p>
                      <p className="text-navy-800 dark:text-white font-medium">{value}</p>
                    </div>
                  ))}

                  {submission.status === 'pending' && (
                    <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-[#1e3a5f]">
                      <p className="text-sm font-semibold text-navy-800 dark:text-white">Review Submission</p>
                      <textarea className="input resize-none text-sm" rows={3}
                        placeholder="Add a note (required if sending back for correction)..."
                        value={reviewNote} onChange={e => setReviewNote(e.target.value)} />
                      <div className="flex gap-3">
                        <button onClick={() => handleReview('return')} disabled={reviewing || !reviewNote.trim()}
                          className="flex-1 border border-orange-200 text-orange-600 hover:bg-orange-50 text-sm font-medium py-2.5 rounded-xl transition-colors disabled:opacity-50">
                          {reviewing ? '...' : '↩ Send Back for Correction'}
                        </button>
                        <button onClick={() => handleReview('approve')} disabled={reviewing}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-2.5 rounded-xl transition-colors disabled:opacity-50">
                          {reviewing ? '...' : '✓ Approve & Save'}
                        </button>
                      </div>
                    </div>
                  )}
                  {submission.status === 'approved' && (
                    <div className="space-y-3">
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                        <p className="text-green-600 font-semibold text-sm">✓ Approved by {submission.reviewedBy}</p>
                        <p className="text-xs text-gray-400 mt-1">{submission.reviewedAt ? new Date(submission.reviewedAt).toLocaleDateString('en-IN') : ''}</p>
                      </div>
                      <button onClick={() => setShowCreateStaff(v => !v)}
                        className="w-full border-2 border-dashed border-orange-200 dark:border-orange-700/30 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/10 text-sm font-medium py-2.5 rounded-xl transition-colors">
                        {showCreateStaff ? '✕ Cancel' : '+ Create Staff Account'}
                      </button>
                      {showCreateStaff && (
                        <div className="space-y-3 border border-gray-100 dark:border-[#1e3a5f] rounded-xl p-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Staff Account</p>
                          <div>
                            <label className="label">Full Name</label>
                            <input className="input text-sm" value={staffForm.name} onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))} />
                          </div>
                          <div>
                            <label className="label">Email</label>
                            <input className="input text-sm" type="email" value={staffForm.email} onChange={e => setStaffForm(f => ({ ...f, email: e.target.value }))} />
                          </div>
                          <div>
                            <label className="label">Username</label>
                            <input className="input text-sm" value={staffForm.username} onChange={e => setStaffForm(f => ({ ...f, username: e.target.value }))} placeholder="e.g. john.doe" />
                          </div>
                          <div>
                            <label className="label">Role</label>
                            <select className="input text-sm" value={staffForm.roleName} onChange={e => setStaffForm(f => ({ ...f, roleName: e.target.value }))}>
                              {roles.map((r: any) => <option key={r.roleName} value={r.roleName}>{r.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="label">Initial Password</label>
                            <input className="input text-sm" type="password" value={staffForm.password} onChange={e => setStaffForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
                          </div>
                          <button onClick={async () => {
                            if (!staffForm.name || !staffForm.username || !staffForm.password || !staffForm.roleName) {
                              toast.error('Please fill all fields')
                              return
                            }
                            setCreatingStaff(true)
                            try {
                              await api.post('/staff', staffForm)
                              toast.success(`Staff account created for ${staffForm.name}`)
                              setShowCreateStaff(false)
                              setSelected(null)
                              setSubmission(null)
                            } catch (e: any) {
                              toast.error(e.response?.data?.error || 'Failed to create account')
                            }
                            setCreatingStaff(false)
                          }} disabled={creatingStaff}
                            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                            {creatingStaff ? 'Creating...' : 'Create Staff Account'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {submission.status === 'returned' && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
                      <p className="text-orange-600 font-semibold text-sm mb-1">↩ Sent back for correction</p>
                      <p className="text-xs text-gray-600 dark:text-gray-300">{submission.hrNote}</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}